import { logger } from '@/lib/logger';

// NOTE: the sort half of this module (AiActionsSortMode + its get/set) was
// retired 2026-09-02 with the Priority/Date toggle — every AI Actions
// surface now renders priority groups with newest-first inside. The stored
// record's stale `mode` key is simply ignored on read.
const STORAGE_KEY_PREFIX = 'ahoy-ai-actions-sort-prefs-';

export type AiActionsView = 'open' | 'done' | 'dismissed' | 'archived';

export const DEFAULT_AI_ACTIONS_VIEW: AiActionsView = 'open';

const VALID_VIEWS: ReadonlySet<string> = new Set<AiActionsView>([
  'open',
  'done',
  'dismissed',
  'archived',
]);

function getStorageKey(userId: number, orgId: number): string {
  return `${STORAGE_KEY_PREFIX}${userId}-${orgId}`;
}

interface AiActionsPrefsRecord {
  mode?: unknown;
  view?: unknown;
}

function readRecord(userId?: number, orgId?: number): AiActionsPrefsRecord {
  if (userId === undefined || orgId === undefined) {
    return {};
  }

  try {
    const raw = localStorage.getItem(getStorageKey(userId, orgId));
    if (!raw) {
      return {};
    }
    return JSON.parse(raw) as AiActionsPrefsRecord;
  } catch (err) {
    logger.warn('Failed to read ai actions prefs from localStorage', {
      component: 'ai-actions-sort-prefs',
      error: String(err),
    });
    return {};
  }
}

export function getAiActionsViewPref(
  userId: number | undefined,
  orgId: number | undefined
): AiActionsView {
  const record = readRecord(userId, orgId);
  return typeof record.view === 'string' && VALID_VIEWS.has(record.view)
    ? (record.view as AiActionsView)
    : DEFAULT_AI_ACTIONS_VIEW;
}

export function setAiActionsViewPref(
  userId: number,
  orgId: number,
  view: AiActionsView
): void {
  try {
    const current = readRecord(userId, orgId);
    localStorage.setItem(
      getStorageKey(userId, orgId),
      JSON.stringify({ ...current, view })
    );
  } catch (err) {
    logger.warn('Failed to persist ai actions view pref to localStorage', {
      component: 'ai-actions-sort-prefs',
      error: String(err),
    });
  }
}

export function clearAiActionsSortPref(userId: number, orgId: number): void {
  try {
    localStorage.removeItem(getStorageKey(userId, orgId));
  } catch (err) {
    logger.warn('Failed to clear ai actions sort pref from localStorage', {
      component: 'ai-actions-sort-prefs',
      error: String(err),
    });
  }
}
